// Lazy-loaded route components.
// All feature pages are code-split to keep the initial bundle lean.
// Only AppLayout, Sidebar, and TopBar load eagerly (imported directly in router/index.tsx).

import { lazy } from 'react';

export const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
export const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
export const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'));
export const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'));
export const ChangePasswordPage = lazy(() => import('@/features/auth/pages/ChangePasswordPage'));
export const MfaSignInPage = lazy(() => import('@/features/auth/pages/MfaSignInPage'));
export const SecurityPage = lazy(() => import('@/features/auth/pages/SecurityPage'));
export const AcceptInvitePage = lazy(() => import('@/features/auth/pages/AcceptInvitePage'));
export const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));

// Documents
export const DocumentListPage = lazy(() => import('@/features/documents/pages/DocumentListPage'));
export const DocumentCreatePage = lazy(
  () => import('@/features/documents/pages/DocumentCreatePage'),
);
export const DocumentDetailPage = lazy(
  () => import('@/features/documents/pages/DocumentDetailPage'),
);

// Customers
export const CustomerListPage = lazy(() => import('@/features/customers/pages/CustomerListPage'));
export const CustomerDetailPage = lazy(
  () => import('@/features/customers/pages/CustomerDetailPage'),
);

// GL / Accounts
export const AccountsPage = lazy(() => import('@/features/gl/pages/AccountsPage'));
export const AccountDetailPage = lazy(() => import('@/features/gl/pages/AccountDetailPage'));
export const AccountStatementPage = lazy(() => import('@/features/gl/pages/AccountStatementPage'));
export const BalanceSummaryPage = lazy(() => import('@/features/gl/pages/BalanceSummaryPage'));
export const GlLedgerPage = lazy(() => import('@/features/gl/pages/GlLedgerPage'));
export const ManualGlEntryPage = lazy(() => import('@/features/gl/pages/ManualGlEntryPage'));
export const TransactionDetailPage = lazy(
  () => import('@/features/gl/pages/TransactionDetailPage'),
);
export const TransactionTypesPage = lazy(() => import('@/features/gl/pages/TransactionTypesPage'));

// Tools (Task 208)
export const BalanceCalculationPage = lazy(
  () => import('@/features/tools/pages/BalanceCalculationPage'),
);
export const FxRatesPage = lazy(() => import('@/features/tools/pages/FxRatesPage'));
export const FxPositionPage = lazy(() => import('@/features/treasury/pages/FxPositionPage'));

// Fees
export const PricingPlansPage = lazy(() => import('@/features/fees/pages/PricingPlansPage'));
export const PricingPlanDetailPage = lazy(
  () => import('@/features/fees/pages/PricingPlanDetailPage'),
);
export const FeeScheduleDetailPage = lazy(
  () => import('@/features/fees/pages/FeeScheduleDetailPage'),
);
export const FeeSimulatorPage = lazy(() => import('@/features/fees/pages/FeeSimulatorPage'));

// Workflows
export const WorkflowListPage = lazy(() => import('@/features/workflows/pages/WorkflowListPage'));
export const WorkflowDetailPage = lazy(
  () => import('@/features/workflows/pages/WorkflowDetailPage'),
);
// Workflow Studio (track 315)
export const WorkflowDraftDesignerPage = lazy(
  () => import('@/features/admin/workflowStudio/pages/WorkflowDraftDesignerPage'),
);

// Assignments (task 402.9; replaced the never-written workflow task queue)
export const MyAssignmentsPage = lazy(
  () => import('@/features/assignments/pages/MyAssignmentsPage'),
);

// Reference Data
export const ReferenceTablesPage = lazy(
  () => import('@/features/reference/pages/ReferenceTablesPage'),
);
export const ReferenceTableDetailPage = lazy(
  () => import('@/features/reference/pages/ReferenceTableDetailPage'),
);

// Financial Reports (Task 176)
export const BalanceSheetPage = lazy(() => import('@/features/reports/pages/BalanceSheetPage'));
export const ProfitAndLossPage = lazy(() => import('@/features/reports/pages/ProfitAndLossPage'));
export const TrialBalancePage = lazy(() => import('@/features/reports/pages/TrialBalancePage'));
export const HoldsReportPage = lazy(() => import('@/features/reports/pages/HoldsReportPage'));
export const TrialBalanceByAccountPage = lazy(
  () => import('@/features/reports/pages/TrialBalanceByAccountPage'),
);
export const SafeguardingPage = lazy(() => import('@/features/reports/pages/SafeguardingPage'));
export const CapitalRequirementsPage = lazy(
  () => import('@/features/reports/pages/CapitalRequirementsPage'),
);

// Admin — Schema Studio (Tasks 314.1 + 314.2)
// Task 422.9: batch import profiles (global admin).
export const ImportProfileListPage = lazy(
  () => import('@/features/admin/importProfiles/ImportProfileListPage'),
);
export const ImportProfileEditorPage = lazy(
  () => import('@/features/admin/importProfiles/ImportProfileEditorPage'),
);
export const SchemaStudioListPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/SchemaStudioListPage'),
);
export const SchemaTypeDetailPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/SchemaTypeDetailPage'),
);
export const SchemaDraftDesignerPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/SchemaDraftDesignerPage'),
);
export const SchemaObjectsPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/SchemaObjectsPage'),
);
export const ObjectDraftDesignerPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/ObjectDraftDesignerPage'),
);
export const SchemaDictionariesPage = lazy(
  () => import('@/features/admin/schemaStudio/pages/SchemaDictionariesPage'),
);

// Admin — Tenant Management (Task 235)
export const AdminTenantListPage = lazy(
  () => import('@/features/admin/tenants/pages/TenantListPage'),
);
export const AdminTenantDetailPage = lazy(
  () => import('@/features/admin/tenants/pages/TenantDetailPage'),
);

// Admin — User & Group Management (Task 236)
export const AdminUserListPage = lazy(() => import('@/features/admin/users/pages/UserListPage'));
export const AdminUserDetailPage = lazy(
  () => import('@/features/admin/users/pages/UserDetailPage'),
);
export const AdminGroupListPage = lazy(() => import('@/features/admin/groups/pages/GroupListPage'));
export const AdminGroupDetailPage = lazy(
  () => import('@/features/admin/groups/pages/GroupDetailPage'),
);
export const AdminRolesPage = lazy(() => import('@/features/admin/roles/pages/RolesPage'));
// Admin — Access (task 401.14)
export const AdminAccessPage = lazy(() => import('@/features/admin/access/pages/AccessPage'));

// Integration Engine admin (Task 239.25)
export const IntegrationDashboardPage = lazy(
  () => import('@/features/integration/pages/IntegrationDashboardPage'),
);
export const DeadLetterQueuePage = lazy(
  () => import('@/features/integration/pages/DeadLetterQueuePage'),
);
export const ConnectorsPage = lazy(() => import('@/features/integration/pages/ConnectorsPage'));
export const ConnectorHealthPage = lazy(
  () => import('@/features/integration/pages/ConnectorHealthPage'),
);
export const RequestAuditPage = lazy(() => import('@/features/integration/pages/RequestAuditPage'));
export const WebhookLogPage = lazy(() => import('@/features/integration/pages/WebhookLogPage'));
export const ReconciliationPage = lazy(
  () => import('@/features/integration/pages/ReconciliationPage'),
);
export const TenantFlagsPage = lazy(() => import('@/features/integration/pages/TenantFlagsPage'));
export const ClearBankSettingsPage = lazy(
  () => import('@/features/integration/pages/ClearBankSettingsPage'),
);

// Settings (admin)
export const TenantSettingsPage = lazy(() => import('@/features/config/pages/TenantSettingsPage'));
export const TransactionLimitsPage = lazy(
  () => import('@/features/config/pages/TransactionLimitsPage'),
);
export const WorkflowPoliciesPage = lazy(
  () => import('@/features/config/pages/WorkflowPoliciesPage'),
);
export const ChartOfAccountsEditorPage = lazy(
  () => import('@/features/admin/chartOfAccounts/ChartOfAccountsPage'),
);
export const ReportMappingsPage = lazy(() => import('@/features/config/pages/ReportMappingsPage'));
// Task 439: per-product settlement accounts for card netting.
export const CardProductRoutingPage = lazy(
  () => import('@/features/config/pages/CardProductRoutingPage'),
);
// Task 438.2: the tenant's layer over the platform chart.
export const AccountCodeLayerPage = lazy(
  () => import('@/features/config/pages/AccountCodeLayerPage'),
);
